import type React from 'react';

import { Link, Text } from '@change/ds-core-ui';
import { InfoIcon, InfoV2Icon } from '@change/ds-icons';

import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import { useIsVideoFirstArm } from '#src/app/pages/actions/shared/hooks';

/**
 * Terms-of-service notice for the video capture flow.
 *
 * Extracted from the recorder so the preview screen can show the same notice with the same string
 * (CHANGE-97687 AC 14). Production only showed it while recording, which meant a supporter who
 * picked a video off their device never saw it before submitting.
 */
type Props = {
	/**
	 * The preview runs a step quieter than the recorder. Only the video-first arms render the notice
	 * there, so this needs no arm check of its own.
	 */
	tone?: 'recorder' | 'preview';
};

export const TermsDisclaimer: React.FC<Props> = ({ tone = 'recorder' }) => {
	// Gated rather than swapped outright: this notice also renders on a screen shared with control,
	// which keeps production's 24px `InfoV2Icon`.
	const isVideoFirstArm = useIsVideoFirstArm();
	const isPreview = tone === 'preview';

	// `Link` carries its own type size and defaults to `medium`, ignoring the paragraph around it -
	// which is why it reads larger than its sentence in production. Matched on the variant arms only.
	const linkSize = (() => {
		if (isPreview) {
			return 'x-small' as const;
		}
		return isVideoFirstArm ? ('small' as const) : ('medium' as const);
	})();

	const icon = (() => {
		if (isPreview) {
			return <InfoIcon className="text-icon-weak mt-0.5 h-3.5 w-3.5 shrink-0" />;
		}
		if (isVideoFirstArm) {
			return <InfoIcon className="text-icon-weak mt-0.5 h-4 w-4 shrink-0" />;
		}
		return <InfoV2Icon size={24} />;
	})();

	return (
		<div data-testid="legal-terms" className="text-typography-weak flex gap-2">
			{icon}
			<Text
				variant="body"
				size={isPreview ? 'x-small' : 'small'}
				className={isPreview ? 'text-typography-weak' : 'text-typography-strong'}
			>
				<TranslateHtml
					value="corgi.petition_actions.supporter_voice_video.record.policy.description"
					replacements={{
						termsOfServiceLink: (
							<Link
								href="https://www.change.org/policies/terms-of-service#3"
								size={linkSize}
								data-qa="terms-of-service"
								data-testid="terms-of-service"
							>
								<Translate value="corgi.petition_actions.supporter_voice_video.record.policy.terms_of_service" />
							</Link>
						),
					}}
				/>
			</Text>
		</div>
	);
};
